import React from 'react';
import PropTypes from 'prop-types';

import AccordionItem from './AccordionItem';
import AccordionHeading from './AccordionHeading';
import AccordionContent from './AccordionContent';

export default class Accordion extends React.PureComponent {
    static propTypes = {
        /** id used to construct the aria relationship between the heading and
         * content. It's only necessary to provide an id if there's more than
         * one Accordion on the page.  */
        id: PropTypes.string,

        /** Expended to be one or more AccordionItems. Use children or items, but not both. */
        children: PropTypes.node,

        /** The accordion item headings and contents supplied in an array. Use items or children, but not both.  */
        items: PropTypes.arrayOf(PropTypes.shape({
            heading: PropTypes.node,
            content: PropTypes.node
        })),

        /** The index of the AccordionItem that should be initially open on first render. */
        defaultActiveIndex: PropTypes.number,

        /** If more than one accordion item can be open at the same time. */
        shouldAllowMultipleOpen: PropTypes.bool,

        /** Aligns the contents with the heading text instead of the icon. */
        hasIndentedBody: PropTypes.bool,

        /** The heading tag to use for all items. */
        headingLevel: PropTypes.number,

        /** The heading style for all items. */
        headingAppearance: PropTypes.oneOf(['h4', 'h5', 'h5Caps', 'h6']),

        /** The index of the open AccordionItem. Set to null if all items should
         * be closed. Use only if externally controlling Accordion. */
        activeIndex: PropTypes.number,

        /** The change handler if externally controlling Accordion. */
        onChange: PropTypes.func,

        /** An additional class added to the outer-most container. */
        className: PropTypes.string
    };

    static defaultProps = {
        children: undefined,
        activeIndex: undefined,
        className: undefined,
        defaultActiveIndex: undefined,
        hasIndentedBody: true,
        headingAppearance: 'h6',
        headingLevel: 3,
        id: 'accordion',
        items: undefined,
        onChange: undefined,
        shouldAllowMultipleOpen: false
    };

    state = {
        activeIndex: this.props.defaultActiveIndex || null // null = all items collapsed
    }

    isControlled() {
        return this.props.activeIndex !== undefined
    }

    handleHeadingClick = (index) => {
        const { onChange } = this.props;

        if (this.isControlled()) {
            onChange(index);
        }
        else {
            this.setState({
                activeIndex: index === this.state.activeIndex ? null : index
            });
        }
    }

    renderChildren() {
        const {
            activeIndex,
            children,
            hasIndentedBody,
            headingAppearance,
            headingLevel,
            id,
            shouldAllowMultipleOpen
        } = this.props;

        return React.Children.map(children, (child, index) => (
            React.cloneElement(child, {
                handleHeadingClick: () => {
                    this.handleHeadingClick(index)
                },
                hasIndentedBody,
                headingAppearance,
                headingLevel,
                id: `${id}-item-${index}`,
                isControlled: !shouldAllowMultipleOpen,
                isExpanded: this.isControlled()
                    ? index === activeIndex
                    : index === this.state.activeIndex
            })
        ));
    }

    renderItems() {
        const {
            activeIndex,
            hasIndentedBody,
            headingAppearance,
            headingLevel,
            id,
            items,
            shouldAllowMultipleOpen
        } = this.props;

        return items.map((item, index) => (
            <AccordionItem
                key={item.heading}
                handleHeadingClick={() => {
                    this.handleHeadingClick(index)
                }}
                hasIndentedBody={hasIndentedBody}
                headingAppearance={headingAppearance}
                headingLevel={headingLevel}
                id={`${id}-item-${index}`}
                isControlled={!shouldAllowMultipleOpen}
                isExpanded={this.isControlled()
                    ? index === activeIndex
                    : index === this.state.activeIndex}>
                <AccordionHeading>
                    {item.heading}
                </AccordionHeading>
                <AccordionContent>
                    {item.content}
                </AccordionContent>
            </AccordionItem>
        ))
    }

    render() {
        const { items, className, id } = this.props;

        return (
            <div data-component="Accordion" className={className} id={id}>
                {items && items.length > 0
                    ? this.renderItems()
                    : this.renderChildren()}
            </div>
        );
    }
}